import { endOfDay, max as maxDate, startOfDay } from 'date-fns'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  BID_SPREAD_ANALYSIS_EARLIEST_DATE,
  BidSpreadAnalysisPeriod,
  resolveBidSpreadAnalysisJobValueRange,
} from 'components/reports/Metrics/constants/BidSpreadAnalysis'
import { BidSpreadAnalysisInputDtoInput } from 'generated/graphql'

import { resolveMetricsPeriod } from '../constants'
import { BidSpreadAnalysisFilterValues } from './BidSpreadAnalysisFilterCriteria'

interface QueryInput {
  start: Date | null
  end: Date | null
  hasValidRange: boolean
  isCustom: boolean
  period: BidSpreadAnalysisPeriod
  input: BidSpreadAnalysisInputDtoInput
}

export const useBidSpreadAnalysisQueryInput = (): QueryInput => {
  const { control } = useFormContext<BidSpreadAnalysisFilterValues>()
  const period = useWatch({ control, name: 'period' })
  const customStart = useWatch({ control, name: 'customStart' })
  const customEnd = useWatch({ control, name: 'customEnd' })
  const selectedServiceCodes = useWatch({ control, name: 'selectedServiceCodes' })
  const vehicleType = useWatch({ control, name: 'vehicleType' })
  const jobValueFilter = useWatch({ control, name: 'jobValueFilter' })
  const customMinJobValue = useWatch({ control, name: 'customMinJobValue' })
  const customMaxJobValue = useWatch({ control, name: 'customMaxJobValue' })

  const isCustom = period === ('custom' as BidSpreadAnalysisPeriod)
  const hasCompleteCustomRange = isCustom && customStart != null && customEnd != null

  const { start: rawStart, end } = hasCompleteCustomRange
    ? { start: startOfDay(customStart), end: endOfDay(customEnd) }
    : resolveMetricsPeriod(period)

  const start = rawStart ? maxDate([rawStart, BID_SPREAD_ANALYSIS_EARLIEST_DATE]) : null

  const hasValidRange = start != null && end != null && start <= end

  const { min: minJobValue, max: maxJobValue } = resolveBidSpreadAnalysisJobValueRange(
    jobValueFilter,
    customMinJobValue ? Number(customMinJobValue) : null,
    customMaxJobValue ? Number(customMaxJobValue) : null,
  )

  return {
    start,
    end,
    hasValidRange,
    isCustom,
    period,
    input: {
      start: start ?? new Date(),
      end: end ?? new Date(),
      xeroCodeIds: selectedServiceCodes?.length
        ? selectedServiceCodes.map((option) => option.value)
        : null,
      vehicleType,
      minJobValue,
      maxJobValue,
    },
  }
}
